import * as THREE from 'three';
import { createSlime, type SlimeMesh } from '../character/Slime';
import { SlimeAnimator } from '../character/SlimeAnimator';
import type { CharacterState } from '../character/CharacterStateMachine';

const VALID_ANIMS: CharacterState[] = ['Idle', 'Walk', 'Run', 'Jump', 'Fall', 'Land', 'Grab'];

function makeNameSprite(name: string): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  const tex = new THREE.CanvasTexture(canvas);
  const draw = (text: string): void => {
    if (!ctx) return;
    ctx.clearRect(0, 0, 256, 64);
    ctx.font = 'bold 30px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 6;
    ctx.strokeStyle = 'rgba(0,0,0,0.7)';
    ctx.strokeText(text, 128, 32);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(text, 128, 32);
    tex.needsUpdate = true;
  };
  draw(name);
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(1.8, 0.45, 1);
  sprite.position.y = 1.45;
  (sprite as { setName?: (n: string) => void }).setName = draw;
  return sprite;
}

/** Visual pemain lain: slime + label nama, posisi di-lerp dari network. */
export class RemoteSlime {
  readonly group = new THREE.Group();
  readonly id: string;
  private mesh: SlimeMesh;
  private animator: SlimeAnimator;
  private label: THREE.Sprite;
  private target = new THREE.Vector3();
  private targetYaw = 0;
  private hasTarget = false;

  constructor(id: string, name: string, colorHex: number) {
    this.id = id;
    this.mesh = createSlime(0.55, 0.9, colorHex);
    this.animator = new SlimeAnimator(this.mesh);
    this.animator.play('Idle');
    this.group.add(this.mesh.group);
    this.label = makeNameSprite(name);
    this.group.add(this.label);
  }

  setName(name: string): void {
    const fn = (this.label as { setName?: (n: string) => void }).setName;
    if (fn) fn(name);
  }

  setColor(hex: number): void {
    this.mesh.setColor(hex);
  }

  setTarget(x: number, y: number, z: number, yaw: number, anim: string): void {
    this.target.set(x, y, z);
    this.targetYaw = yaw;
    const state: CharacterState = (VALID_ANIMS as string[]).includes(anim)
      ? (anim as CharacterState)
      : 'Idle';
    this.animator.play(state);
    this.animator.setGrounded(state !== 'Jump' && state !== 'Fall');
    if (!this.hasTarget) {
      this.group.position.copy(this.target);
      this.group.rotation.y = yaw;
      this.hasTarget = true;
    }
  }

  update(delta: number): void {
    if (this.hasTarget) {
      const f = 1 - Math.exp(-10 * delta);
      this.group.position.lerp(this.target, f);
      let diff = this.targetYaw - this.group.rotation.y;
      diff = Math.atan2(Math.sin(diff), Math.cos(diff));
      this.group.rotation.y += diff * f;
    }
    this.animator.update(delta);
  }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.group);
    this.mesh.bodyGeo.dispose();
    (this.mesh.body.material as THREE.Material).dispose();
    (this.label.material as THREE.Material).dispose();
    ((this.label.material as THREE.SpriteMaterial).map as THREE.Texture | null)?.dispose();
  }
}
